<template>
  <div>
    <CustomCallout
      :has-toggle="requireDocuments"
      class="mt-2 deep"
      :border-color-hex="value.statusColor"
      :controlled="selected"
      :opened="selected"
    >
      <template #default>
        <div class="row mt-2 pb-3">
          <div class="col-md-2 pt-3">
            <div
              class="col-8 rounded-pill p-2 text-center"
              :style="{ backgroundColor: value.statusColor }"
            >
              <span class="font-weight-bold">{{ value.statusName }}</span>
            </div>
          </div>
          <div class="col-md-9 pt-3">
            <div class="row">
              <div class="col-md-4">
                <div class="info-text-title">Nombre del Estudiante:</div>
                <p>{{ `${value.studentSieId} - ${value.studentName}` }}</p>
              </div>
              <div class="col-md-1">
                <div class="info-text-title">Edad:</div>
                <p>{{ value.studentAge }}</p>
              </div>
              <div class="col-md-2">
                <div class="info-text-title">Grado:</div>
                <p>{{ value.studentGrade }}</p>
              </div>
              <div class="col-md-2">
                <div class="info-text-title">Impedimento:</div>
                <p>{{ value.impediment }}</p>
              </div>
              <div class="col-md-3">
                <div class="info-text-title">Necesidad(es):</div>
                <p>{{ necesities }}</p>
              </div>
            </div>
          </div>
          <div class="col-1 d-flex align-items-center">
            <CustomCheckbox
              v-model="selected"
              type="checkbox"
              background-color="white"
              :disabled="disabled"
            />
          </div>

        </div>
      </template>
      <template #edition v-if="requireDocuments && value.documents.length">
        <div class="additional-container">
          <div class="p2 mt-1">
            <div class="bg-gray p-2">
              <div class="bg-white p-3 rounded pl-c-xl">
                <span class="dot-icon mr-2">
                  <i class="fa fa-list-alt text-orange text-center" />
                </span>
                <span class="font-18 font-weight-bold">Documentos requeridos</span>
                <CustomCallout
                  v-for="(document, index) in value.documents"
                  :key="`row-document-${index}`"
                  :has-toggle="false"
                  class="mt-3"
                >
                  <div class="row pt-2">
                    <div class="col-md-2 col-sm-6">
                      <div class="info-text-title">
                        Necesidad:
                      </div>
                      <p class="font-weight-bold">
                        {{ document.necesityName }}
                      </p>
                    </div>
                    <div class="col-md-6 col-sm-6">
                      <div class="info-text-title">
                        Documento:
                      </div>
                      <p class="font-weight-bold">
                        {{ document.documentTypeName }}
                      </p>
                    </div>
                    <div class="col-md-4 col-sm-12">
                      <div class="font-weight-bold pt-2">
                        <ValidationProvider
                          v-slot="{ errors }"
                          :name="`${document.documentTypeName} ${index}`"
                          :rules="document.rules"
                        >
                          <CustomFileInput
                            v-model="document.file"
                            class="fixed-label-width"
                            :attachment-id="document.itemAttachmentId"
                            :name="!document.file ? document.filename : document.file.name"
                            :on-download="downloadFile"
                            :accept="'.pdf'"
                            :max-size="10"
                            :error="errors[0]"
                            custom-error-msg="Favor anejar documento."
                          />
                        </ValidationProvider>
                      </div>
                    </div>
                  </div>
                </CustomCallout>
                <CustomResultNotFound
                  v-if="value.documents && !value.documents.length"
                  class="mt-4 row pt-3"
                  text="No documentos requeridos para esta necesidad."
                />
              </div>
            </div>
          </div>
        </div>
      </template>
    </CustomCallout>

    <WarningMessage v-if="showCustodianWarning">
      <span>El maestro seleccionado no cuenta con el estudiante asignado, <span class="custodian-redirect" @click="custodianRedirect">presione aquí</span> para añadir al estudiante.</span>
    </WarningMessage>
  </div>
</template>

<script>
import CustomCallout from "custom-callout";
import CustomFileInput from 'CustomFileInput';
import CustomResultNotFound from "custom-result-not-found";
import CustomCheckbox from "@/components/common/CustomCheckbox";
import necessityCodes from '@/utils/constants/hrrequest/necessityCodes';
import peiServiceNeedCodes from "@/utils/constants/hrrequest/peiServiceNeedCodes";
import documentNecessityConfigs from "@/utils/constants/hrrequest/documentNecessityConfigs";
import utilitiesService from "@/services/hr/utilities";
import documentsService from '@/services/documents';
import WarningMessage from "../components/WarningMessage.vue";

export default {
  components: {
    CustomCallout,
    CustomFileInput,
    CustomCheckbox,
    CustomResultNotFound,
    WarningMessage
  },
  props: {
    value: {
      type: Object,
      required: true,
      default: () => ({
        impediment: null,
        necesities: null,
        saeE10Code: null,
        saeE10Id: null,
        statusColor: null,
        statusId: null,
        statusName: null,
        studentAge: null,
        studentGrade: null,
        studentName: null,
        studentSieId: null,
        selected: false,
        documents: [],
      })
    },
    custodianStudents: {
      type: Array,
      default: null,
    },
    disabled: false,
    requireDocuments: false,
    collections: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selected: this.value.selected
    };
  },
  computed: {
    showCustodianWarning() {
      return this.custodianStudents != null ? !this.custodianStudents.includes(this.value.studentSieId) : false;
    },
    necesities() {
      return this.value.necesities.map(necessity => necessity.name).join(', ');
    },
  },
  watch: {
    'value.selected'(newValue) {
      this.selected = newValue;
    },
    selected(newValue) {
      this.$emit('toggle-select', this.value.saeE10Id, newValue);
    },
  },
  methods: {
    getDocumentType(documentTypeId) {
      return (
        this.collections.documentTypes.find(
          (docType) => docType.id === documentTypeId
        ) || {}
      );
    },
    getNecessityDocuments(necessityId) {
      const documentConfig = documentNecessityConfigs.find(
        (config) => config.necessityId == necessityId
      );
      if (!documentConfig) return [];

      const necesity = this.collections.necessities.find(n => n.id == documentConfig.necessityId)
      return documentConfig.documentTypes.map((docType) => {
        const document = utilitiesService.createDocument
          .bind(this, docType.id, docType.rules)
          .call();

          return {
            ...document,
            necesityName: necesity.name,
            necessityId: necesity.id
          };
      });
    },
    createDocuments() {
      const peiCodes =  this.value.necesities.map(necessity => necessity.id);
      const needs = Array.from(new Set(this.value.necesities.map(necessity => necessity.value)));

      return needs.reduce((acc, id) => {
        let documentsData = this.value.documents?.filter(doc => doc.necessityId == id) ?? [];
        const necessityDocuments = this.getNecessityDocuments(id);
        const uploadedDocumentTypeIds = documentsData.map(x => x.documentTypeId);
        const pendingUploadDocuments = necessityDocuments.filter(x => !uploadedDocumentTypeIds.includes(x.documentTypeId));
        pendingUploadDocuments.forEach(x => documentsData.push(x));
        
        if (id === necessityCodes.health && !peiCodes.includes(peiServiceNeedCodes.healthAssistantCateterism)) {
          documentsData = []; // Si es de salud pero no cateterizacion no debe requerir documentos
        }
        return [...acc, ...documentsData];
      }, []);
    },
    downloadFile(attachmentId) {
      return documentsService.downloadFromServer(attachmentId);
    },
    custodianRedirect() {
      this.$emit('custodian-redirect');
    },
  },
  beforeMount() {
    this.value.documents = this.requireDocuments ? this.createDocuments() : [];
  }
};
</script>
<style lang="scss" scoped>
@use "sass:map";
@import '@/assets/scss/variables/_colors.scss';

.info-text-title {
  color: map.get($colors, 'gray-6');
  font-size: 1rem;
  font-weight: normal;
}

.dot-icon {
  box-shadow: 0px 3px 6px #00000029;
  margin: 2px;
  padding: 5px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border-style: solid;
  border-width: 3px;
  border-color: #fff;
}

.custodian-redirect {
  cursor: pointer;
  font-weight: bold;
  text-decoration: underline #af6708;
}

::v-deep .fixed-label-width > div > div > div{
  flex-basis: 50%;
}
</style>
